'use client';

import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { apiSwagger } from '@/shared/api';
import { Album } from '@/sharedTypes/Api';
import samplesModel, { SAMPLES_MODE } from '../SamplesModel';
import { PiHeartFill, PiHeartLight, PiMusicNotes } from 'react-icons/pi';
import { cn } from '@/lib/utils';
import favoritesStore from '@/stores/favoritesStore';
import { FavoriteType } from '@/types/favorites';
import { signIn, useSession } from 'next-auth/react';

interface AlbumsListViewProps {
  areFavoritesShown: boolean;
  albumTypes: string[];
  emptyMessage: string;
  emptyFavMessage: string;
}

const AlbumsListView = observer(({ areFavoritesShown, albumTypes, emptyMessage, emptyFavMessage }: AlbumsListViewProps) => {
  const [albums, setAlbums] = useState<Album[]>([]);
  const [loading, setLoading] = useState(true);
  const isUserLoggedIn = useSession().data?.accessToken;

  useEffect(() => {
    const fetchAlbums = async () => {
      try {
        const res = await apiSwagger.albums.albumsList();
        const data = await res.json();
        setAlbums((data.albums || []).filter((a: Album) => albumTypes.includes(a.albumType)));
      } catch (error) {
        console.error('Failed to fetch albums:', error);
      } finally {
        setLoading(false);
        document.dispatchEvent(new CustomEvent('albumListLoaded'));
      }
    };
    fetchAlbums();
  }, []);

  const handleAlbumClick = (albumId: string) => {
    samplesModel.previousMode = samplesModel.currentMode;
    samplesModel.selectedAlbumId = albumId;
    samplesModel.currentMode = SAMPLES_MODE.ALBUM_DETAIL;
  };

  if (loading) {
    return (
      <div className='grid grid-cols-2 gap-3 p-3'>
        {[...Array(4)].map((_, i) => (
          <div key={i} className='animate-pulse'>
            <div className='aspect-[3/4] bg-zinc-200 rounded-lg' />
            <div className='h-4 bg-zinc-200 rounded mt-2 w-3/4' />
          </div>
        ))}
      </div>
    );
  }

  const displayedAlbums = areFavoritesShown
    ? albums.filter((a) => favoritesStore.favoriteItems.some((fav) => fav.id === a._id))
    : albums;

  if (displayedAlbums.length === 0) {
    return (
      <div className='text-sm text-zinc-500 text-center py-8'>
        {areFavoritesShown ? emptyFavMessage : emptyMessage}
      </div>
    );
  }

  return (
    <div className='grid grid-cols-2 gap-3 p-3'>
      {displayedAlbums.map((album) => (
        <div
          key={album._id}
          data-testid={`album-card-${album.title}`}
          className='cursor-pointer group'
          onClick={() => handleAlbumClick(album._id)}
        >
          <div className='aspect-[3/4] bg-zinc-100 rounded-lg overflow-hidden relative'>
            {album.coverImage ? (
              <img
                src={album.coverImage}
                alt={album.title}
                className='w-full h-full object-cover'
              />
            ) : (
              <div className='w-full h-full flex items-center justify-center text-zinc-300'>
                <PiMusicNotes size={48} />
              </div>
            )}
            <button
              data-testid={`album-fav-btn-${album.title}`}
              data-favorited={favoritesStore.isFavorite(album._id)}
              onClick={(e) => {
                e.stopPropagation();
                isUserLoggedIn
                  ? favoritesStore.toggleSingleFavorite(album._id, FavoriteType.ALBUM)
                  : signIn('keycloak');
              }}
              className={cn(
                'absolute top-2 right-2 p-1.5 rounded-full bg-white/80 backdrop-blur-sm',
                'transition-opacity hover:text-accent-blue',
                favoritesStore.isFavorite(album._id) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
              )}
            >
              {favoritesStore.isFavorite(album._id) ? (
                <PiHeartFill size={18} color='#0c8ce9' />
              ) : (
                <PiHeartLight size={18} />
              )}
            </button>
          </div>
          <p className='text-sm mt-1.5 truncate max-w-[90%]' title={album.title}>
            {album.title}
          </p>
        </div>
      ))}
    </div>
  );
});

export default AlbumsListView;
